/**
 * Schemas Zod compartilhados entre API (validação de entrada) e Web
 * (formulários). Um só lugar para a forma dos dados; tRPC infere o resto.
 */
import { z } from 'zod';
import { TIPOS_EVENTO } from '../apontamento/maquina-estados.js';
import { PERIODICIDADES_BONUS } from '../avaliacao/avaliar-periodo.js';
import { codigoVinculoValido, normalizarCodigoVinculo } from '../dispositivos/vinculo.js';
import { DESTINOS_FALTA } from '../fichas/falta.js';
import { PERIODOS } from '../tempo/periodo.js';
export const idSchema = z.string().min(1);
export const loginSchema = z.object({
    // Normaliza antes de validar: " Lilian@Rekar.com " → "lilian@rekar.com".
    email: z.string().trim().toLowerCase().pipe(z.email('E-mail inválido')),
    senha: z.string().min(6, 'Senha deve ter ao menos 6 caracteres'),
});
/**
 * Login no tablet: operador toca no próprio nome e digita o PIN (4 a 6
 * dígitos). Identificar a pessoa antes do PIN evita colisão de PINs e
 * custa uma única verificação argon2.
 */
export const loginPostoSchema = z.object({
    postoId: idSchema,
    operadorId: idSchema,
    pin: z.string().regex(/^\d{4,6}$/, 'PIN deve ter de 4 a 6 dígitos'),
});
/** Código de vínculo como a pessoa digita no tablet; normalizado antes de validar. */
export const codigoVinculoSchema = z
    .string()
    .transform(normalizarCodigoVinculo)
    .refine(codigoVinculoValido, 'Código inválido: são 6 letras ou números');
/** Tablet troca o código gerado na gestão por um token de dispositivo (ADR 0010). */
export const vincularDispositivoSchema = z.object({
    codigo: codigoVinculoSchema,
    /** Nome livre para a gestão reconhecer o aparelho (ex.: "Tablet Samsung preto"). */
    nome: z.string().trim().min(1, 'Informe um nome').max(60, 'Máximo de 60 caracteres'),
});
export const gerarCodigoVinculoSchema = z.object({ postoId: idSchema });
/**
 * Ajudante entra na ficha com o próprio PIN (RF-T07). O posto já está
 * logado; só se confirma quem é o colega.
 */
export const verificarPinSchema = loginPostoSchema.omit({ postoId: true });
/** Consultas por período (dia/semana/mês) a partir de uma data de referência. */
export const periodoSchema = z.object({
    periodo: z.enum(PERIODOS).default('dia'),
    /** Qualquer instante dentro do período desejado; padrão: agora. */
    referencia: z.coerce.date().optional(),
});
export const itemPedidoSchema = z.object({
    produtoId: idSchema,
    quantidade: z.int().positive('Quantidade deve ser maior que zero'),
});
/**
 * Campos opcionais seguem a convenção dos cadastros: o formulário manda `null`
 * para "vazio" (`normalizar` em apps/web), então o schema aceita null e nunca
 * coage null em data (`z.coerce.date()` sozinho transformaria null em 1970).
 */
export const criarPedidoSchema = z.object({
    origemId: idSchema,
    /** Identificação externa (nº do pedido no ERP/marketplace), opcional. */
    referenciaExterna: z.string().trim().max(60).nullable().default(null),
    prazo: z.coerce.date().nullable().default(null),
    observacao: z.string().trim().max(500).nullable().default(null),
    itens: z.array(itemPedidoSchema).min(1, 'Pedido precisa de ao menos um item'),
});
/** Correção dos dados do pedido (não mexe nos itens: para isso, cancela a ficha ou lança outro). */
export const atualizarPedidoSchema = criarPedidoSchema
    .omit({ itens: true })
    .extend({ id: idSchema });
/**
 * Lista de pedidos da gestão. `situacao` "abertos" = aberto + em produção.
 * `ordem` "recentes" = do mais novo para o mais antigo (tela de pedidos);
 * "prazo" = próxima entrega primeiro (diretoria).
 */
export const listarPedidosSchema = z
    .object({
    status: z.enum(['ABERTO', 'EM_PRODUCAO', 'CONCLUIDO', 'CANCELADO']).optional(),
    situacao: z.enum(['abertos', 'concluidos', 'cancelados', 'todos']).optional(),
    busca: z.string().trim().max(60).optional(),
    ordem: z.enum(['prazo', 'recentes']).default('prazo'),
    limite: z.int().min(1).max(200).default(50),
})
    .default({ ordem: 'prazo', limite: 50 });
export const registrarEventoSchema = z.object({
    fichaEtapaId: idSchema,
    tipo: z.enum(TIPOS_EVENTO),
    /** Chave de idempotência gerada no tablet — reenvio offline não duplica. */
    idempotencyKey: z.uuid(),
    /** Instante no relógio do tablet; servidor compara com o próprio. */
    emCliente: z.coerce.date(),
    motivoPausaId: idSchema.optional(),
    quantidade: z.int().nonnegative().optional(),
    /** Ajudante que entrou com a própria senha para dividir a ficha. */
    ajudanteId: idSchema.optional(),
    /** Concluir com menos peças que o esperado: saldo (para depois) ou perda. */
    destinoFalta: z.enum(DESTINOS_FALTA).optional(),
    /** Obrigatório quando o destino é perda. */
    motivoPerdaId: idSchema.optional(),
});
/**
 * Início ou fim de uma atividade sem ficha, apontado no tablet (passa pela
 * mesma fila offline dos eventos). O fim aponta para a chave do início, que
 * o tablet já conhece mesmo sem rede.
 */
export const apontarAtividadeSchema = z.discriminatedUnion('acao', [
    z.object({
        acao: z.literal('iniciar'),
        atividadeId: idSchema,
        idempotencyKey: z.uuid(),
        emCliente: z.coerce.date(),
    }),
    z.object({
        acao: z.literal('encerrar'),
        chaveInicio: z.uuid(),
        idempotencyKey: z.uuid(),
        emCliente: z.coerce.date(),
    }),
]);
export const criterioAvaliacaoSchema = z.object({
    codigo: z
        .string()
        .trim()
        .min(1, 'Informe o código')
        .max(30)
        .regex(/^[a-z][a-z0-9_]*$/, 'Use letras minúsculas, números e _'),
    nome: z.string().trim().min(1, 'Informe o nome').max(60),
    peso: z.number().nonnegative('Peso não pode ser negativo'),
    ativo: z.boolean().default(true),
});
export const faixaPremiacaoSchema = z.object({
    codigo: z
        .string()
        .trim()
        .min(1, 'Informe o código')
        .max(30)
        .regex(/^[a-z][a-z0-9_]*$/, 'Use letras minúsculas, números e _'),
    nome: z.string().trim().min(1, 'Informe o nome').max(60),
    notaMinima: z.number().min(1, 'Nota mínima vai de 1 a 10').max(10, 'Nota mínima vai de 1 a 10'),
});
/** Peso de um critério para um setor e/ou produto (RF-D06); ao menos um dos dois. */
export const pesoCriterioSchema = z
    .object({
    criterioId: idSchema,
    setorId: idSchema.nullable().default(null),
    produtoId: idSchema.nullable().default(null),
    peso: z.number().nonnegative('Peso não pode ser negativo'),
})
    .refine((p) => p.setorId != null || p.produtoId != null, {
    path: ['setorId'],
    message: 'Escolha um setor, um produto ou os dois',
});
const fracao = (msg) => z.number().min(0, msg).max(1, msg);
/** Parâmetros de avaliação (RF-D01, D05, I07): tudo que o cliente ajusta sem código. */
export const configuracaoAvaliacaoSchema = z
    .object({
    curvaExperienciaDias: z.int().nonnegative('Dias não podem ser negativos'),
    toleranciaInicial: z
        .number()
        .min(0, 'Tolerância entre 0 e 99%')
        .lt(1, 'Tolerância entre 0 e 99%'),
    semaforoVerdeMin: fracao('Limite entre 0 e 100%'),
    semaforoAmareloMin: fracao('Limite entre 0 e 100%'),
    periodicidadeBonus: z.enum(PERIODICIDADES_BONUS),
    exibirIndividualNoPainel: z.boolean().default(false),
    exibirIndicadorAoOperador: z.boolean().default(true),
    /** Atraso tolerado sem desconto na pontualidade, em minutos. */
    pontualidadeToleranciaMin: z.int().nonnegative('Minutos não podem ser negativos'),
    /** Atraso a partir do qual a pontualidade do dia vale zero, em minutos. */
    pontualidadeAtrasoMaximoMin: z.int().positive('Informe minutos maiores que zero'),
    /** Regras de falta (contrato 8.1). */
    atestadoContaComoFalta: z.boolean().default(false),
    faltaEntraNaNota: z.boolean().default(false),
    faltasParaPerderFaixa: z
        .int()
        .positive('Use 1 ou mais (vazio = nunca)')
        .nullable()
        .default(null),
})
    .refine((c) => c.semaforoAmareloMin <= c.semaforoVerdeMin, {
    path: ['semaforoAmareloMin'],
    message: 'O limite do âmbar não pode passar o do verde',
})
    .refine((c) => c.pontualidadeAtrasoMaximoMin > c.pontualidadeToleranciaMin, {
    path: ['pontualidadeAtrasoMaximoMin'],
    message: 'O atraso máximo precisa ser maior que a tolerância',
});
/** Competência do fechamento mensal: "YYYY-MM". */
export const competenciaSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use o formato AAAA-MM');
export const gerarFechamentoSchema = z.object({ competencia: competenciaSchema });
/** Ciência do fechamento no tablet (RF-T12): o operador logado confirma com o próprio PIN. */
export const darCienciaSchema = z.object({
    fechamentoId: idSchema,
    pin: z.string().regex(/^\d{4,6}$/, 'PIN deve ter de 4 a 6 dígitos'),
});
//# sourceMappingURL=index.js.map